import { useEffect, useState } from "react";
import type { FeedPost, Playlist } from "@gooner/shared";
import { FEED_CAPTION_MAX } from "@gooner/shared";
import { track } from "../../../analytics";
import { userApi } from "../../../lib/api";
import { useSession } from "../../../lib/session";
import { MediaPicker, type MediaPick, isUploadPick, pickId } from "../../../components/MediaPicker";
import { AuthModal } from "../AuthModal";
import { Button, cx } from "../../ui/primitives";
import { MediaSurface } from "../../ui/MediaSurface";
import { MASK, Masked } from "../../../lib/replayMask";

/**
 * Where a status is posted. It reads like a status box — a prompt, then a text field
 * when opened — and a clip goes WITH it: the words are the status, the clip is what
 * you are gooning to. The clip is not optional (owner, 2026-09-13: "you're posting
 * your status, you just put a clip on it"): the button stays dark until one is picked,
 * and the server refuses the same way. The picker is the Community Wall's shelves.
 */
export function FeedComposer({ onPosted }: { onPosted: (post: FeedPost) => void }) {
  const { user, ready } = useSession();
  const [authOpen, setAuthOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [picks, setPicks] = useState<MediaPick[]>([]);
  // The OTHER thing a status can carry (2026-09-13): one of your public collections.
  // Picking one clears the clip and vice versa — a post carries one or the other.
  const [kind, setKind] = useState<"clip" | "collection">("clip");
  const [collections, setCollections] = useState<Playlist[] | null>(null);
  const [collection, setCollection] = useState<Playlist | null>(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ABOVE THE EARLY RETURNS, and it has to stay there. React counts hooks per render, so
  // an effect written below `if (!user)` runs on the signed-in render and not on the
  // signed-out one — and `useSession` is a store, so signing in re-renders THIS fiber
  // instead of remounting it. The count went 10 -> 11 mid-life and React threw #310
  // (2026-09-23). The signed-out case is answered inside the effect, not by its position.
  useEffect(() => {
    if (!ready || !user) return;
    if (kind !== "collection" || collections !== null) return;
    let cancelled = false;
    userApi
      .listPlaylists()
      // Your OWN, and only the public ones — the server refuses anything else, and a
      // shelf of unshareable rows is a shelf of dead ends. Reserved ids (faps, uploads)
      // are never public, so they fall out here too.
      .then((r) => !cancelled && setCollections(r.playlists.filter((p) => p.public)))
      .catch(() => !cancelled && setCollections([]));
    return () => {
      cancelled = true;
    };
  }, [kind, collections, ready, user]);

  if (!ready) return null;
  if (!user) {
    return (
      <div className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
        <button
          type="button"
          onClick={() => setAuthOpen(true)}
          className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-center text-sm font-semibold text-black hover:opacity-90"
        >
          Sign in to post
        </button>
        <p className="mt-2 text-xs text-zinc-500">A post carries your name, so it lives on an account.</p>
        {authOpen ? <AuthModal initialMode="register" reason="Create a free account to post on the Feed." onClose={() => setAuthOpen(false)} /> : null}
      </div>
    );
  }

  const pick = picks[0] ?? null;
  const canPost = kind === "clip" ? pick !== null : collection !== null;
  const post = async () => {
    if (!canPost || busy) return;
    setBusy(true);
    setError(null);
    const upload = kind === "clip" && pick !== null && isUploadPick(pick);
    try {
      const r = await userApi.feedPost(
        kind === "collection" && collection ? { collection: collection.id } : { clip: pickId(pick!) },
        caption.trim(),
      );
      track("feed_post_created", { upload, collection: kind === "collection", caption: caption.trim().length > 0 });
      setPicks([]);
      setCollection(null);
      setCaption("");
      setOpen(false);
      onPosted(r.post);
    } catch (err) {
      const status = err instanceof Error ? Number(/^(\d{3})/.exec(err.message)?.[1]) : undefined;
      track("feed_post_failed", { status: status || 0 });
      const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
      setError(msg || "That didn't post. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] px-4 py-3 text-left text-sm text-zinc-400 transition-colors hover:border-[var(--color-accent)]/60 hover:text-zinc-200"
      >
        {/* Your own username: masked in session replay (lib/replayMask); the prompt stays. */}
        What are you gooning to, <Masked>{user.username}</Masked>?
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
      <label className="block">
        <span className="mb-1 flex items-center justify-between text-xs text-zinc-400">
          <span>Your status</span>
          <span className={caption.length > FEED_CAPTION_MAX ? "text-red-400" : "text-zinc-600"}>
            {caption.length}/{FEED_CAPTION_MAX}
          </span>
        </span>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.currentTarget.value)}
          rows={3}
          maxLength={FEED_CAPTION_MAX + 20}
          autoFocus
          placeholder={`What are you gooning to, ${user.username}?`}
          // The placeholder carries the username, and maskAllInputs masks the value but
          // not the placeholder: the marker makes maskAttributeFn mask it too.
          {...MASK}
          className="w-full resize-none rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-3 py-2 text-base text-zinc-100 placeholder:text-zinc-600 sm:text-sm"
        />
      </label>
      <p className="mb-1 mt-3 flex flex-wrap items-center gap-2 text-sm font-medium text-zinc-200">
        Put something on it
        <span className="inline-flex gap-1">
          {(["clip", "collection"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={cx(
                "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                kind === k ? "bg-zinc-100 text-black" : "bg-[var(--color-panel-2)] text-zinc-300 hover:text-white",
              )}
            >
              {k === "clip" ? "A clip" : "A collection"}
            </button>
          ))}
        </span>
        <span className="text-xs font-normal text-zinc-500">
          {kind === "clip"
            ? pick
              ? "picked — pick another to swap it"
              : "every status carries a clip or a collection"
            : collection
              ? <>sharing <Masked>{collection.name}</Masked></>
              : "one of your public collections"}
        </span>
      </p>
      {kind === "clip" ? (
        <MediaPicker
          picks={picks}
          onChange={setPicks}
          max={1}
          uploadNote="Until then only you can see the post."
          onTab={(tab) => track("feed_picker_tab", { tab })}
          onUploadStarted={(bytes) => track("feed_upload_started", { bytes })}
        />
      ) : (
        <CollectionShelf list={collections} picked={collection} onPick={setCollection} />
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="primary"
          disabled={!canPost || busy || caption.length > FEED_CAPTION_MAX}
          onClick={() => void post()}
        >
          {busy ? "Posting…" : "Post"}
        </Button>
        <Button
          onClick={() => {
            setOpen(false);
            setPicks([]);
            setCollection(null);
            setCaption("");
            setError(null);
          }}
        >
          Cancel
        </Button>
        {error ? <span className="text-xs text-red-400">{error}</span> : null}
      </div>
    </div>
  );
}

/** Your public collections, as a pick-one shelf. Cover from the collection's own
 *  thumbnail (server-resolved, fail-closed like every poster). */
/** Exported for tools/checks/replay-mask.mts (the shelf renders without the composer's
 *  open state, which a server render cannot reach). */
export function CollectionShelf({
  list,
  picked,
  onPick,
}: {
  list: Playlist[] | null;
  picked: Playlist | null;
  onPick: (p: Playlist | null) => void;
}) {
  if (list === null) return <p className="py-4 text-sm text-zinc-500">Loading your collections…</p>;
  if (list.length === 0) {
    return (
      <p className="py-4 text-sm text-zinc-500">
        None of your collections is public yet. Open one under Collections and flip it public to share it here.
      </p>
    );
  }
  return (
    <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
      {list.map((p) => {
        const on = picked?.id === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(on ? null : p)}
            className={cx(
              "flex flex-col overflow-hidden rounded-lg border text-left ring-2 ring-inset transition-colors",
              on ? "border-transparent ring-[var(--color-accent)]" : "border-[var(--color-edge)] ring-transparent hover:border-zinc-500",
            )}
          >
            <div className="aspect-[4/3] w-full bg-black">
              {p.thumbnailPosterUrl ? <MediaSurface kind="image" src={p.thumbnailPosterUrl} fit="cover" className="h-full w-full" /> : null}
            </div>
            <div className="px-2 py-1.5">
              <div className="truncate text-sm font-medium text-zinc-100" {...MASK}>{p.name}</div>
              <div className="text-[11px] text-zinc-500">
                {p.clipCount} clip{p.clipCount === 1 ? "" : "s"} · public
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
